import React, { useState } from "react";
import Card from "features/shared/components/card/Card";
import CapacityYearSelect from 'features/contracts/components/reservedCapacity/CapacityYearSelect';
import ContractFullDTO from 'features/contracts/dtos/ContractFullDTO';
import RolesChecker from "features/customer/components/RolesChecker";
import { getReservedCapacityChangeRoute } from "features/shared/helpers/routeHelper";
import { useHistory } from "react-router-dom";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IReservedCapacityFormService from "features/requirements/services/IReservedCapacityFormService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import ReservedCapacityLocationState from 'features/contracts/models/ReservedCapacityLocationState';
import { getStartYear } from "features/contracts/helpers/contractDateHelper";
import CapacityElectricitySection from "./CapacityElectricitySection";
import CapacityGasSection from "./CapacityGasSection";
import CapacityGasDraftSection from "./CapacityGasDraftSection";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import DropdownActionItem from "features/shared/components/dropdown/DropdownActionItem";
import { isEnabledCustomForm } from "features/requirements/helpers/requirementTypeHelper";

interface Props {
  contract: ContractFullDTO;
}

const ReservedCapacitySection = ({ contract }: Props) => {
  //#region translations
  const tLabels = useTranslation("contracts\\contracts").t;  
  //#endregion translations
  
  //#region DI
  const reservedCapacityformService = useInjection<IReservedCapacityFormService>(IocTypes.ReservedCapacityFormService);
  //#endregion DI

  //#region router
  const history = useHistory<ReservedCapacityLocationState>();
  //#region router

  //#region state
  const [selectedYear, setSelectedYear] = useState<number>(getStartYear(contract.effectiveDate!, contract.terminationDate!));
  //#endregion state

  //#region handlers
  const yearChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  }
  
  const changeReservedCapacityHandler = (capacityType?: "monthly" | "yearly") => {
    history.push({
      pathname: getReservedCapacityChangeRoute(contract.contractId!, contract.contractType!, capacityType),
      state: {
        selectedYear: selectedYear
      }
    });    
  }
  //#endregion handlers

  //#region sub-renders
  const renderCapacityListingSections = () => { 
    if (contract.contractType == "ElectricityHighVoltage") {
      return (
        <CapacityElectricitySection 
          contractId={contract.contractId!}
          year={selectedYear}
        />
      );
    } else if (contract.contractType == "GasLargeConsumption") {
      return (
        <>
          <CapacityGasDraftSection 
            contractId={contract.contractId!}
            year={selectedYear}
            bottomTitle={tLabels("reservedCapacitySection.validValues")}
          />
          <CapacityGasSection 
            contractId={contract.contractId!}
            year={selectedYear}
          />
        </>
      );
    }
  }

  const renderChangeReservedCapacityBtn = () => {
    let boundaries = reservedCapacityformService.getContractYearBoundaries(contract.terminationDate!, contract.contractDurationType!);
    let startYear = boundaries[0];
    let endYear = boundaries[1];

    if (selectedYear >= startYear && selectedYear <= endYear) {
      if (contract.contractType == "ElectricityHighVoltage" && (isEnabledCustomForm("ZMENAKAPACITYELEKTRINAROCNI") && isEnabledCustomForm("ZMENAKAPACITYELEKTRINAMESICNI"))) {
        return (
          <RolesChecker enabledRoles="Contributor">
            {renderChangeElectricityCapacityDropdown()}
          </RolesChecker>
        );
      } else if (contract.contractType == "GasLargeConsumption" && isEnabledCustomForm("ZMENAKAPACITYPLYN")) {
        return (
          <RolesChecker enabledRoles="Contributor">
            <button className="btn btn-sm btn-secondary" onClick={() => changeReservedCapacityHandler()}>
              {tLabels("reservedCapacitySection.changeReservedCapacity")}
            </button>
          </RolesChecker>
        );
      } 
    }
  }

  const renderChangeElectricityCapacityDropdown = () => {
    return (
      <Dropdown>
        <Dropdown.Toggle id="profile-data-dropdown" size='sm' variant='secondary' className='mt-3 mt-sm-0' block>
          <span className="mr-1">
            {tLabels("reservedCapacitySection.changeReservedCapacity")}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu >
          <DropdownActionItem 
            title={tLabels("reservedCapacitySection.changeMonthlyCapacity")} 
            onClick={() => changeReservedCapacityHandler("monthly")}
          />
          <DropdownActionItem
            title={tLabels("reservedCapacitySection.changeYearlyCapacity")} 
            onClick={() => changeReservedCapacityHandler("yearly")}
          />
        </Dropdown.Menu>
      </Dropdown>
    );
  }
  //#endregion sub-renders

  //#region render
  return (
    <>
      <Card className="p-3 mb-4">
        <div className="row no-gutters align-items-center">
          <div className="col-auto pr-3">
            <div className="d-flex align-items-center">
              <span className="mr-2 text-muted">
                {tLabels("reservedCapacitySection.year")}:
              </span>
              <CapacityYearSelect 
                startDate={contract.effectiveDate!}
                endDate={contract.terminationDate!}
                selectedYear={selectedYear}
                onChange={yearChangeHandler}
              />
            </div>
          </div>
          <div className="col-auto ml-auto">
            {renderChangeReservedCapacityBtn()}
          </div>
        </div>
      </Card>
      {renderCapacityListingSections()}
    </>
  );
  //#endregion render
};

export default ReservedCapacitySection;
